import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";

import {
  TABLE_MAIL_TOKEN_LIST,
  MAIL_TOKEN_LIST_ENDPOINT,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import MailTokenId from "./components/MailTokenId/MailTokenId";
import MailTokenName from "./components/MailTokenName/MailTokenName";
import MailTokenTemplates from "./components/MailTokenTemplates/MailTokenTemplates";
import MailTokenDescription from "./components/MailTokenDescription/MailTokenDescription";
import Action from "./components/Action/Action";
import { columnHeader } from "@utils/utils";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createNewMailToken,
    editMailToken,
  } = props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const dataURL = MAIL_TOKEN_LIST_ENDPOINT;

  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ cell: { value }, row: { original } }) => (
        <MailTokenId
          data={value}
          editMailToken={editMailToken}
          fullData={original}
        />
      ),
    },
    {
      Header: t("NAME"),
      title: "NAME",
      accessor: "name",
      disableFilters: true,
      Cell: ({ cell: { value }, row: { original } }) => (
        <MailTokenName
          data={value}
          editMailToken={editMailToken}
          fullData={original}
        />
      ),
    },
    {
      Header: t("EMAIL_TEMPLATES"),
      title: "EMAIL_TEMPLATES",
      accessor: "templates",
      Cell: ({ cell: { value } }) => <MailTokenTemplates data={value} />,
      disableFilters: true,
    },
    {
      Header: t("DESCRIPTION"),
      title: "DESCRIPTION",
      accessor: "description",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <MailTokenDescription data={value} />,
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value } }) => (
        <Action
          data={value}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
          editMailToken={editMailToken}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    globalFilter,
    pageSize,
    currentPage,
    sort,
    appliedFilters,
  }) => {
    setRequestData({
      pageSize,
      currentPage,
      sort,
      globalFilter,
      appliedFilters,
    });

    // Set the loading state
    setLoading(true);
    axios
      .get(
        `/general/mailTokens?limit=${pageSize}&page=${currentPage}&orderBy=${
          sort ? sort.dir : "asc"
        }&orderColumn=${sort ? sort.column : "id"}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      )
      .then((response) => {
        if (response?.data) {
          const mailTokenList = response.data.mailTokens;
          const responseData = mailTokenList.map((token) => {
            const tempData = { ...token };
            tempData.action = { ...tempData };
            tempData.id = tempData.id;
            tempData.name = tempData.name;
            tempData.templates = {
              templates: tempData.templates || [],
            };
            tempData.description = tempData.description;
            return tempData;
          });
          setData([...responseData]);
          setPageCount(response.data.filteredRecords);
        } else {
          setData([]);
          setPageCount(0);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching mail tokens:", error);
        setData([]);
        setPageCount(0);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  return (
    <EncloseTable
      type={TABLE_MAIL_TOKEN_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={50}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      createNewAction={createNewMailToken}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  createNewMailToken: PropTypes.func.isRequired,
  editMailToken: PropTypes.func.isRequired,
};

export default withTranslation()(PrepareDataTable);
